/**
 * ChatAsideView Component
 * Compact chat view for the Aside panel
 */

import { useTrudChat } from "@/hooks/useTrudChat";
import { useChatStore } from "@/stores/chatStore";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInput } from "./ChatInput";
import { Button } from "@/components/ui/button";
import { LoaderButton } from "@/components/LoaderButton";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import {
  Maximize2,
  MessageSquarePlus,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import { useAuthUser } from "@/context/auth/AuthContext";
import { hasModule } from "@/context/auth/authz";

export const ChatAsideView = () => {
  const navigate = useNavigate();
  const store = useChatStore();
  const user = useAuthUser();
  const [isComposing, setIsComposing] = useState(false);

  // `ai/chat` es feature comercial: hoy NO está en `modules` (el BFF debe incluirla
  // ahí tras eliminar `businessPlanFeatures`) → hasta entonces queda deshabilitada.
  const hasAIFeature = hasModule(user, "ai/chat");

  const {
    messages,
    sendMessage,
    stopGeneration,
    isLoading,
    isStreaming,
    currentAgentType,
  } = useTrudChat({
    conversationId: hasAIFeature ? store.activeConversationId : null,
    onConversationCreated: (id) => {
      store.setActiveConversation(id);
    },
  });

  // Reset composing state when there's an active conversation or messages
  useEffect(() => {
    if (store.activeConversationId || messages.length > 0) {
      setIsComposing(false);
    }
  }, [store.activeConversationId, messages.length]);

  const handleOpenFullPage = () => {
    navigate("/chat");
  };

  const handleNewChat = () => {
    // Reset to empty state and show input - backend will create conversation on first message
    store.setActiveConversation(null);
    setIsComposing(true);
  };

  // No AI feature - show locked state
  if (!hasAIFeature) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-3 border-b border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Sparkles className="h-4 w-4 text-text-secondary flex-shrink-0" />
            <span
              className="text-sm font-medium truncate text-text-secondary"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Trud Agentic
            </span>
          </div>
        </div>

        {/* Locked Empty State */}
        <div className="flex-1 overflow-auto p-4">
          <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-background-200 flex items-center justify-center border border-border">
              <Lock className="h-5 w-5 text-text-secondary" />
            </div>
            <div className="space-y-1.5">
              <p
                className="text-base font-light tracking-tight"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Trud Agentic
              </p>
              <p className="text-xs text-text-secondary leading-relaxed px-2">
                Tu plan actual no incluye el asistente de inteligencia
                artificial. Contacta a soporte o actualiza tu plan para
                habilitar esta funcionalidad.
              </p>
            </div>
          </div>
        </div>

        {/* Disabled Input */}
        <div className="border-t border-border p-3 opacity-50 pointer-events-none">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <span className="text-sm text-text-secondary flex-1">
              Escribe un mensaje...
            </span>
            <Lock className="h-3.5 w-3.5 text-text-secondary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Compact Header */}
      <div className="p-3 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Sparkles className="h-4 w-4 text-brand-primary flex-shrink-0" />
          <span
            className="text-sm font-medium truncate"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Trud Agentic
          </span>
          {isStreaming && (
            <span className="flex items-center gap-1 text-xs text-text-secondary">
              <span className="animate-pulse">●</span>
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleNewChat}
            title="Nueva conversación"
            disabled={!store.activeConversationId && messages.length === 0}
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleOpenFullPage}
            title="Abrir en página completa"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Messages or Empty State */}
      {!store.activeConversationId && messages.length === 0 && !isComposing ? (
        <div className="flex-1 overflow-auto p-4">
          {/* Compact Empty State */}
          <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-gradient flex items-center justify-center shadow-md">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="space-y-1.5">
              <p
                className="text-base font-light tracking-tight"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Chateá con Trud Agentic
              </p>
              <p className="text-xs text-text-secondary leading-relaxed px-2">
                Preguntá sobre tu negocio, generá reportes o buscá información
                al instante.
              </p>
            </div>
            <LoaderButton
              onClick={handleNewChat}
              size="sm"
              isLoading={false}
              className="rounded-full px-5"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Iniciar chat
            </LoaderButton>
          </div>
        </div>
      ) : (
        <>
          {/* Messages List - Compact */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {isLoading && messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ChatMessageList
                messages={messages}
                agentType={currentAgentType}
                compact
                isStreaming={isStreaming}
                isLoading={isLoading}
                className="h-full"
              />
            )}
          </div>

          {/* Input - Compact */}
          <ChatInput
            onSubmit={sendMessage}
            disabled={isLoading}
            isStreaming={isStreaming}
            onStopGeneration={stopGeneration}
            placeholder="Escribe un mensaje..."
            className="border-t"
            compact
          />
        </>
      )}

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-border bg-background-50 text-center">
        <button
          type="button"
          onClick={handleOpenFullPage}
          className="text-xs text-text-secondary hover:text-foreground transition-colors"
        >
          Ver historial completo →
        </button>
      </div>
    </div>
  );
};
